/*
  La Matcha — blog page.

    <blog-list>  "show more": fetches the next page of the section (Section Rendering API), appends
                 its cards to the grid and swaps the pager. Without JavaScript the numbered
                 pagination inside <noscript> does the same job.

  Attributes:
    data-section-id        id of the section to request
  Children:
    [data-blog-grid]       list that receives the cards
    [data-blog-pager]      progress ([data-blog-bar]), the "show more" link and the pagination fallback
    a[data-blog-more]      link to the next page; data-loaded-text is announced after loading
    [data-blog-numbered]   numbered pagination of a page that was opened directly; dropped once
                           more cards are appended, because its "current page" no longer holds
*/
(() => {
  if (customElements.get('blog-list')) return;

  const LM = window.LM || {};

  class BlogList extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const more = event.target.closest('a[data-blog-more]');
        if (!more || !this.contains(more)) return;
        event.preventDefault();
        this.loadMore(more);
      });
    }

    async loadMore(link) {
      if (this.loading) return;
      const grid = this.querySelector('[data-blog-grid]');
      const pager = this.querySelector('[data-blog-pager]');
      if (!grid || !pager || !LM.fetchDocument) {
        window.location.assign(link.href);
        return;
      }
      this.loading = true;
      link.classList.add('is-loading');
      link.setAttribute('aria-disabled', 'true');
      try {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const doc = await LM.fetchDocument(url.toString());
        const nextGrid = doc.querySelector('[data-blog-grid]');
        const nextPager = doc.querySelector('[data-blog-pager]');
        if (!nextGrid) throw new Error('empty');

        const added = [...nextGrid.children];
        const message = link.getAttribute('data-loaded-text');
        grid.append(...added);

        if (nextPager) {
          const oldBar = pager.querySelector('[data-blog-bar]');
          const before = oldBar ? oldBar.style.getPropertyValue('--value') : '';
          nextPager.querySelectorAll('[data-blog-numbered], noscript').forEach((el) => el.remove());
          pager.innerHTML = nextPager.innerHTML;
          // The progress bar grows from its previous value.
          const bar = pager.querySelector('[data-blog-bar]');
          if (bar && before) {
            const after = bar.style.getPropertyValue('--value');
            bar.style.setProperty('--value', before);
            void bar.offsetWidth;
            bar.style.setProperty('--value', after);
          }
        } else {
          pager.remove();
        }

        // Keyboard and screen reader users continue from the first new card.
        const first = added[0] && added[0].querySelector('a[href]:not([tabindex="-1"])');
        if (first) first.focus({ preventScroll: true });
        if (message && LM.announce) LM.announce(message);
      } catch (error) {
        window.location.assign(link.href);
      } finally {
        this.loading = false;
        if (link.isConnected) {
          link.classList.remove('is-loading');
          link.removeAttribute('aria-disabled');
        }
      }
    }
  }

  customElements.define('blog-list', BlogList);
})();
